import { useEffect } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { io } from 'socket.io-client';

import { api } from '@/shared/services/api/endpoints';
import { httpClient } from '@/shared/services/api/http-client';
import { queryKeys } from '@/shared/services/api/query-keys';
import type { NotificationType } from '@/shared/services/api/types';
import {
  reportServerReachable,
  verifyServerReachable,
} from '@/shared/services/network/connectivity';
import { setRealtimeSocket } from '@/shared/services/realtime/realtime-bus';
import { realtimeEndpoint } from '@/shared/services/realtime/realtime-endpoint';

import { ENV } from '@/config/env.config';
import { useIsSignedIn, useSessionStore } from '@/stores/session.store';

type Keys = ReadonlyArray<ReadonlyArray<unknown>>;

/**
 * L'ouverture de la page : tout ce que le cache date d'avant vient du disque,
 * écrit lors d'une visite précédente, pendant que le canal était fermé.
 */
const PAGE_OPENED_AT = Date.now();

/** Ce qu'une notification annonce, en clés de cache à faire retomber. */
type NotificationSignal = {
  /** Racines à invalider, quel que soit le contenu. */
  keys?: (data: NotificationData) => Keys;
  /**
   * Le compte lui-même a changé : identité vérifiée, place prise dans une
   * boutique, mesure prononcée. Ce n'est pas du cache, c'est la session : et
   * avec elle ce que l'interface autorise.
   */
  session?: boolean;
};

/** Contexte porté par la notification : des identifiants, et un chemin à ouvrir. */
type NotificationData = {
  orderId?: string;
  disputeId?: string;
  inspectionId?: string;
  shopId?: string;
  conversationId?: string;
};

/**
 * Ce que chaque fait notifié demande de recharger.
 *
 * La notification a déjà résolu la question difficile : **qui** devait être
 * prévenu. Elle arrive donc exactement aux bonnes personnes, et le client n'a
 * plus qu'à en tirer l'écran concerné. C'est ce qui évite un second mécanisme
 * de diffusion, avec son propre calcul de destinataires, à maintenir en
 * parallèle du premier.
 *
 * La table est **complète**, pas partielle : un fait qui n'a rien à rafraîchir
 * au-delà de la liste des notifications s'inscrit `null`, et le compilateur
 * réclame une décision pour chaque type ajouté au serveur. C'est ce qui évite
 * qu'un fait nouveau arrive un jour sans que personne n'ait eu à se demander
 * quel écran il périme.
 */
const ON_NOTIFICATION: Record<NotificationType, NotificationSignal | null> = {
  // Une commande déplace tout ce que le vendeur regarde : ses commandes, mais
  // aussi les chiffres de son tableau de bord, son inventaire et les quantités
  // de son catalogue. Ne recharger que la liste laissait le tableau sur ses
  // chiffres d'avant : « j'ai vendu, et rien ne bouge ».
  ORDER_PLACED: { keys: (data) => tradeKeys(data) },
  ORDER_STATUS_CHANGED: { keys: (data) => [...orderKeys(data), queryKeys.shops.root] },
  ORDER_CANCELLED: { keys: (data) => tradeKeys(data) },
  PAYMENT_DECLARED: { keys: (data) => [...orderKeys(data), queryKeys.shops.root] },
  // Fonds confirmés : les unités réservées sont vendues, l'encaissement entre.
  PAYMENT_CONFIRMED: { keys: (data) => tradeKeys(data) },
  PAYMENT_REJECTED: { keys: (data) => [...orderKeys(data), queryKeys.shops.root] },

  // Un litige déplace aussi la commande : ouverte elle passe en litige,
  // tranchée elle est soldée ou annulée.
  DISPUTE_OPENED: { keys: (data) => disputeKeys(data) },
  // Une partie a complété le dossier : il se relit tel qu'il est maintenant.
  DISPUTE_EVIDENCE_ADDED: { keys: (data) => disputeKeys(data) },
  // Trancher un litige rend, libère ou sort des unités.
  DISPUTE_DECIDED: {
    keys: (data) => [...disputeKeys(data), queryKeys.catalog.root, queryKeys.stock.root],
  },

  INSPECTION_REQUESTED: { keys: (data) => inspectionKeys(data) },
  INSPECTION_ASSIGNED: { keys: (data) => inspectionKeys(data) },
  INSPECTION_SCHEDULED: { keys: (data) => inspectionKeys(data) },
  // Rapport soumis : la mission change d'état chez le demandeur, l'agent et
  // la file de validation de l'administration.
  INSPECTION_SUBMITTED: { keys: (data) => inspectionKeys(data) },
  INSPECTION_CANCELLED: { keys: (data) => inspectionKeys(data) },
  // Une inspection favorable pose le label : la boutique et son catalogue le
  // portent, pas seulement le dossier d'inspection.
  INSPECTION_DECIDED: {
    keys: (data) => [...inspectionKeys(data), queryKeys.shops.root, queryKeys.catalog.root],
  },

  SHOP_VERIFIED: { keys: (data) => shopKeys(data) },
  SHOP_SUSPENDED: { keys: (data) => shopKeys(data) },
  SHOP_MEMBER_REQUEST: { keys: (data) => shopKeys(data) },
  // L'invitation change la fiche de la boutique chez l'invité (le bandeau
  // « accepter / décliner » vient de `myMembership`).
  SHOP_MEMBER_INVITED: { keys: (data) => shopKeys(data) },
  // Entrer dans une boutique, c'est y gagner des droits : la session suit.
  SHOP_MEMBER_DECISION: { keys: (data) => shopKeys(data), session: true },

  INVOICE_ISSUED: { keys: () => [queryKeys.billing.root] },
  INVOICE_OVERDUE: { keys: () => [queryKeys.billing.root, queryKeys.shops.root] },
  INVOICE_PAID: { keys: () => [queryKeys.billing.root, queryKeys.shops.root] },
  // Notification adressée a l'administration : le vendeur qui a déclaré, lui,
  // voit déja sa facture marquée. La liste des factures se rafraichit.
  INVOICE_PAYMENT_DECLARED: { keys: () => [queryKeys.billing.root] },

  URGENT_REQUEST_MATCH: { keys: () => [queryKeys.urgentRequests.root] },
  FOLLOWED_SHOP_LISTING: { keys: () => [queryKeys.listings.root] },
  // Un commentaire ou une réponse : le fil de commentaires de l'annonce, et le
  // compteur que portent ses cartes.
  LISTING_COMMENT: { keys: () => [queryKeys.listings.root] },
  // Une annonce retirée par l'administration : elle quitte le fil et le
  // catalogue de la boutique, sa fiche affiche le motif.
  LISTING_REMOVED: { keys: () => [queryKeys.listings.root, queryKeys.catalog.root] },
  // Une boutique suivie passe en direct : le rail des directs de l'accueil et
  // la liste des directs se rafraîchissent ; toucher la notification ouvre le
  // direct lui-même.
  LIVE_STARTED: { keys: () => [queryKeys.lives.root] },
  // Une alerte de stock change la fiche de stock et l'inventaire ; le tableau
  // de bord porte aussi ses compteurs d'alertes.
  STOCK_LOW: { keys: () => [queryKeys.stock.root, queryKeys.shops.root, queryKeys.catalog.root] },
  STOCK_EXPIRING: { keys: () => [queryKeys.stock.root, queryKeys.shops.root] },
  NEW_MESSAGE: { keys: () => [queryKeys.messaging.root] },
  // L'appel manqué a posé sa ligne dans le fil : la liste et la discussion
  // la montrent.
  CALL_MISSED: { keys: () => [queryKeys.messaging.root] },

  // L'identité vérifiée débloque l'ouverture d'une boutique et la candidature
  // d'agent : sans la session, l'écran continuait d'afficher la porte fermée.
  KYC_REVIEWED: { keys: () => [queryKeys.profile.kyc], session: true },
  AGENT_APPLICATION_DECISION: { keys: () => [queryKeys.profile.agentApplications], session: true },
  // Une mesure change ce que le compte peut faire, et l'état de sa boutique.
  SANCTION_ISSUED: { keys: () => [queryKeys.shops.root, queryKeys.platform.root], session: true },

  /**
   * Compte désactivé. La session tombe dans la foulée (`session:revoked`), donc
   * ce signal arrive rarement a destination — mais quand il y parvient, il doit
   * poser le mur tout de suite : `session` recharge `GET /auth/me`, qui porte
   * désormais la mesure et son message.
   */
  ACCOUNT_SUSPENDED: {
    keys: () => [queryKeys.shops.root, queryKeys.listings.root, queryKeys.platform.root],
    session: true,
  },
  /**
   * Compte rendu. Celui-la arrive a coup sur : la personne est devant le mur,
   * son canal est vivant, et la réactivation ne coupe pas les sessions. Sans
   * `session`, elle serait restée devant un mur levé.
   */
  ACCOUNT_REACTIVATED: {
    keys: () => [queryKeys.shops.root, queryKeys.listings.root, queryKeys.platform.root],
    session: true,
  },

  // Files d'attente de l'administration : elles ne bougent que par ce qui
  // arrive, et personne n'est là pour tirer l'écran vers le bas.
  KYC_SUBMITTED: { keys: () => [queryKeys.admin.kycRoot, queryKeys.platform.root] },
  REPORT_SUBMITTED: { keys: () => [queryKeys.admin.reportsRoot, queryKeys.platform.root] },
  AGENT_APPLICATION_SUBMITTED: {
    keys: () => [queryKeys.admin.agentsRoot, queryKeys.platform.root],
  },

  // Celui qui a signalé apprend l'issue et n'ouvre aucun dossier : la file des
  // signalements est un écran d'administration, et lui n'y a pas accès.
  REPORT_HANDLED: null,

  // Communautés : toutes les clés du domaine partagent la racine
  // `communities` (listes, fiche, membres, publications, commentaires), donc
  // une seule invalidation couvre l'écran ouvert, quel qu'il soit. La demande
  // d'ouverture nourrit aussi les compteurs de l'administration.
  COMMUNITY_REQUEST_SUBMITTED: {
    keys: () => [queryKeys.communities.root, queryKeys.platform.root],
  },
  COMMUNITY_REQUEST_DECIDED: { keys: () => [queryKeys.communities.root] },
  COMMUNITY_JOIN_REQUEST: { keys: () => [queryKeys.communities.root] },
  COMMUNITY_MEMBER_DECISION: { keys: () => [queryKeys.communities.root] },
  COMMUNITY_POST_PENDING: { keys: () => [queryKeys.communities.root] },
  COMMUNITY_POST_DECISION: { keys: () => [queryKeys.communities.root] },
  COMMUNITY_COMMENT: { keys: () => [queryKeys.communities.root] },
  COMMUNITY_MODERATED: { keys: () => [queryKeys.communities.root] },
};

const orderKeys = (data: NotificationData): Keys =>
  data.orderId
    ? [queryKeys.orders.root, queryKeys.orders.detail(data.orderId)]
    : [queryKeys.orders.root];

/**
 * Tout ce qu'une vente déplace : la commande, les chiffres de la boutique
 * (tableau de bord, encaissements, mouvements), l'inventaire et le catalogue.
 */
const tradeKeys = (data: NotificationData): Keys => [
  ...orderKeys(data),
  queryKeys.shops.root,
  queryKeys.stock.root,
  queryKeys.catalog.root,
];

const disputeKeys = (data: NotificationData): Keys => [
  queryKeys.disputes.root,
  ...(data.disputeId ? [queryKeys.disputes.detail(data.disputeId)] : []),
  ...orderKeys(data),
];

const inspectionKeys = (data: NotificationData): Keys => [
  queryKeys.inspections.root,
  ...(data.inspectionId ? [queryKeys.inspections.detail(data.inspectionId)] : []),
];

const shopKeys = (data: NotificationData): Keys => [
  queryKeys.shops.root,
  ...(data.shopId
    ? [queryKeys.shops.detail(data.shopId), queryKeys.shops.members(data.shopId)]
    : []),
  queryKeys.catalog.root,
];

/**
 * Connexion temps réel au serveur (Socket.io, espace `/realtime`).
 *
 * Le socket ne transporte pas de données : il transporte des *signaux*. À
 * chaque signal, on invalide le cache concerné et React Query recharge par
 * l'API : une seule source de vérité, le temps réel ne fait qu'en avancer
 * l'horloge. Un client hors ligne retrouve donc exactement le même état en
 * rouvrant l'application, sans chemin de code séparé.
 *
 * La poignée de main porte le jeton d'accès du compte ; à la reconnexion,
 * socket.io redemande le jeton du moment, pas celui de la première connexion.
 */
export function RealtimeProvider({ children }: { children: React.ReactNode }) {
  const signedIn = useIsSignedIn();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!signedIn) {
      return;
    }

    // Le préfixe de l'API (`/bul-api` en ligne) va dans `path` : dans l'adresse,
    // socket.io le prendrait pour le nom de l'espace.
    const endpoint = realtimeEndpoint(ENV.API_URL);
    const socket = io(endpoint.url, {
      path: endpoint.path,
      transports: ['websocket'],
      auth: (provide) => provide({ token: httpClient.getAccessToken() ?? '' }),
    });

    // Les écrans qui ont besoin du canal lui-même (le tchat d'un direct, son
    // produit épinglé) passent par le bus : ils s'abonnent, ils ne le tiennent pas.
    setRealtimeSocket(socket);

    const invalidate = (...keys: Keys) => {
      for (const queryKey of keys) {
        void queryClient.invalidateQueries({ queryKey: queryKey as Array<unknown> });
      }
    };

    // ------------------------------------------------------------- signaux --

    socket.on('notification:new', (payload: { type?: NotificationType; data?: unknown }) => {
      invalidate(queryKeys.notifications.root);

      const signal = payload?.type ? ON_NOTIFICATION[payload.type] : undefined;
      if (!signal) {
        return;
      }
      const data = (payload.data ?? {}) as NotificationData;
      invalidate(...(signal.keys?.(data) ?? []));
      if (signal.session) {
        void refreshSession();
      }
    });

    // Celui qui vient d'agir n'est pas notifié de son propre geste : c'est par
    // ce signal que son tableau de bord et son inventaire suivent.
    socket.on('order:changed', (payload: { orderId?: string }) => {
      invalidate(...tradeKeys(payload ?? {}));
    });

    socket.on('message:new', (payload: { conversationId?: string }) => {
      invalidate(queryKeys.messaging.conversations);
      if (payload?.conversationId) {
        // Le fil ET son en-tête : une proposition de prix arrive comme un
        // message, mais elle change aussi le produit épinglé et le geste
        // proposé en tête d'écran.
        invalidate(
          queryKeys.messaging.messages(payload.conversationId),
          queryKeys.messaging.conversation(payload.conversationId),
        );
      }
    });

    // Un litige touche trois écrans à la fois (le dossier, la liste, la
    // commande), et vise aussi celui qui vient d'agir : l'arbitrage vient de
    // l'administration, qui n'est partie ni d'un côté ni de l'autre.
    socket.on('dispute:changed', (payload: { disputeId?: string; orderId?: string }) => {
      invalidate(...disputeKeys(payload ?? {}));
    });

    // Le compte est suspendu, bloqué ou supprimé : ses sessions sont tombées.
    // On sort proprement au lieu d'attendre le prochain 401.
    socket.on('session:revoked', () => {
      void useSessionStore.getState().signOut();
    });

    // ----------------------------------------------- reprise après coupure --

    /**
     * Le canal a été coupé : entre-temps, des signaux sont passés sans nous.
     * On ne rejoue pas ce qui a été manqué : on recharge, ce qui donne le même
     * résultat sans journal à tenir côté serveur.
     *
     * Seulement après une coupure : au tout premier branchement, les écrans
     * viennent de charger, et tout relancer coûterait un aller-retour à chaque
     * ouverture de l'application ; sur un réseau lent, cela se voit.
     */
    let interrupted = false;
    let tokenRenewed = false;
    let firstConnection = true;

    socket.on('connect', () => {
      // Le canal répond : le serveur est joignable, les requetes en pause
      // repartent. Avant l'invalidation, pour qu'elle ne parte pas dans le vide.
      reportServerReachable(true);
      tokenRenewed = false;
      if (interrupted) {
        interrupted = false;
        firstConnection = false;
        void queryClient.invalidateQueries();
        return;
      }
      if (firstConnection) {
        firstConnection = false;
        // Sur le web, rouvrir la page est un geste courant (rechargement,
        // nouvel onglet) : ce que le cache a rendu depuis le disque date d'avant
        // l'ouverture, et le canal était fermé entre-temps. On relit ces
        // entrées-là seulement (celles à l'écran tout de suite, les autres à
        // leur prochain affichage) ; ce qui vient d'être chargé reste tel quel.
        void queryClient.invalidateQueries({
          predicate: (query) =>
            query.state.dataUpdatedAt > 0 && query.state.dataUpdatedAt < PAGE_OPENED_AT,
        });
      }
    });
    socket.on('disconnect', () => {
      interrupted = true;
      // Le canal donne l'alerte, une requete la confirme : il peut tomber seul,
      // et le site ne doit pas se dire hors ligne quand le serveur répond.
      // C'est encore lui qui annonce le retour : il réessaie en continu.
      verifyServerReachable();
    });

    /**
     * Poignée de main refusée. Le cas courant n'est pas une panne : c'est un
     * jeton d'accès arrivé à terme pendant que l'application dormait. On le
     * fait tourner **une fois**, et la tentative suivante de socket.io repart
     * avec le nouveau ; en faire tourner un par échec transformerait un serveur
     * injoignable en boucle de rotations. Une session vraiment morte, elle,
     * arrive par `session:revoked`, pas par ici.
     */
    socket.on('connect_error', () => {
      interrupted = true;
      verifyServerReachable();
      if (tokenRenewed) {
        return;
      }
      tokenRenewed = true;
      void httpClient.renewAccessToken();
    });

    /**
     * Retour sur l'onglet, ou retour du réseau. On ne relance pas les requêtes :
     * celles qui comptaient sont arrivées par le canal. On vérifie seulement que
     * le canal, lui, est bien revenu : c'est sa reconnexion qui déclenchera la
     * remise à niveau.
     */
    const wake = () => {
      if (document.visibilityState === 'visible' && !socket.connected) {
        socket.connect();
      }
    };
    document.addEventListener('visibilitychange', wake);
    window.addEventListener('online', wake);

    return () => {
      document.removeEventListener('visibilitychange', wake);
      window.removeEventListener('online', wake);
      setRealtimeSocket(null);
      socket.removeAllListeners();
      socket.disconnect();
      // Déconnexion volontaire (sortie de session) : l'état « injoignable »
      // ne doit pas survivre au canal qui le mesurait.
      reportServerReachable(true);
    };
  }, [signedIn, queryClient]);

  return children;
}

/**
 * Recharge le compte et ses droits.
 *
 * Les permissions viennent du serveur à la connexion et n'étaient plus jamais
 * relues : une identité vérifiée pendant la session laissait l'interface sur
 * l'état d'avant, porte fermée, jusqu'au prochain démarrage.
 */
async function refreshSession(): Promise<void> {
  try {
    const [me, permissions] = await Promise.all([api.auth.me(), api.auth.permissions()]);
    useSessionStore.getState().refreshMe(me, permissions.rules);
  } catch {
    // Le compte reste tel qu'il est connu : une coupure ne doit pas dégrader
    // les droits affichés, et le prochain démarrage remettra tout d'aplomb.
  }
}
